import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const InquiryList = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [reply, setReply] = useState('');
  const [expandedInquiries, setExpandedInquiries] = useState(new Set());
  const [expandedReplies, setExpandedReplies] = useState(new Set());

  // Deletion state
  const [inquiryToDelete, setInquiryToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all'); // all, read, unread

  useEffect(() => {
    fetchInquiries();
  }, [currentPage, itemsPerPage, filterStatus]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/inquiries`, {
        params: { page: currentPage, limit: itemsPerPage, status: filterStatus },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const payload = response.data;
      // Support both the new paginated shape and a plain array (backward compatible)
      if (Array.isArray(payload)) {
        setInquiries(payload);
        setTotal(payload.length);
        setUnreadTotal(payload.filter(inq => !inq.is_read).length);
        setTotalPages(1);
      } else {
        const data = payload.data || [];
        const pagination = payload.pagination || {};
        setInquiries(data);
        setTotal(pagination.total || 0);
        setUnreadTotal(pagination.unreadTotal || 0);
        setTotalPages(pagination.totalPages || 1);

        // If we deleted the last item on a page beyond the first, step back
        if (data.length === 0 && currentPage > 1) {
          setCurrentPage(prev => Math.max(prev - 1, 1));
        }
      }
      // Clear stale selections that are no longer on the page
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      setInquiries([]);
      setTotal(0);
      setUnreadTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowReplyModal(true);
  };

  const sendReply = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/inquiries/${selectedInquiry._id}/reply`, {
        reply: reply
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Refresh inquiries to get updated data
      await fetchInquiries();

      setShowReplyModal(false);
      setReply('');
      setSelectedInquiry(null);
    } catch (error) {
      console.error('Error sending reply:', error);
    }
  };

  const markAsRead = async (inquiryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/inquiries/${inquiryId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update local state
      setInquiries(inquiries.map(inq =>
        inq._id === inquiryId ? { ...inq, is_read: true } : inq
      ));
      setUnreadTotal(prev => Math.max(prev - 1, 0));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = (inquiry) => {
    setInquiryToDelete(inquiry);
    setShowDeleteModal(true);
  };

  const deleteInquiry = async () => {
    if (!inquiryToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/inquiries/${inquiryToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowDeleteModal(false);
      setInquiryToDelete(null);
      await fetchInquiries();
      alert('お問い合わせを削除しました');
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      alert('お問い合わせの削除に失敗しました');
    }
  };

  const toggleSelect = (inquiryId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(inquiryId)) {
        next.delete(inquiryId);
      } else {
        next.add(inquiryId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === inquiries.length && inquiries.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(inquiries.map(inq => inq._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      alert('削除するお問い合わせを選択してください');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/inquiries/bulk-delete`, {
        inquiryIds: Array.from(selectedIds)
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowBulkDeleteModal(false);
      setSelectedIds(new Set());
      await fetchInquiries();
      alert(`${response.data.deletedCount}件のお問い合わせを削除しました`);
    } catch (error) {
      console.error('Error bulk deleting inquiries:', error);
      alert('お問い合わせの一括削除に失敗しました');
    }
  };

  const toggleInquiryExpansion = (inquiryId) => {
    const newExpandedInquiries = new Set(expandedInquiries);
    if (newExpandedInquiries.has(inquiryId)) {
      newExpandedInquiries.delete(inquiryId);
    } else {
      newExpandedInquiries.add(inquiryId);
    }
    setExpandedInquiries(newExpandedInquiries);
  };

  const toggleReplyExpansion = (replyId) => {
    const newExpandedReplies = new Set(expandedReplies);
    if (newExpandedReplies.has(replyId)) {
      newExpandedReplies.delete(replyId);
    } else {
      newExpandedReplies.add(replyId);
    }
    setExpandedReplies(newExpandedReplies);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  const goToPage = (page) => setCurrentPage(page);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  // Generate page numbers for pagination (with ellipsis)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '20px 0',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
            color: currentPage === 1 ? '#6c757d' : '#495057',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          前へ
        </button>

        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} style={{ padding: '8px 4px', color: '#6c757d' }}>...</span>
            ) : (
              <button
                key={page}
                onClick={() => goToPage(page)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  backgroundColor: currentPage === page ? '#007bff' : 'white',
                  color: currentPage === page ? 'white' : '#495057',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: currentPage === page ? 'bold' : 'normal'
                }}
              >
                {page}
              </button>
            )
          ))}
        </div>

        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
            color: currentPage === totalPages ? '#6c757d' : '#495057',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          次へ
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>読み込み中...</div>
      </div>
    );
  }

  const allSelectedOnPage = inquiries.length > 0 && selectedIds.size === inquiries.length;

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>お問い合わせ一覧</h2>

      {/* Summary */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <span style={{ fontSize: '16px', color: '#7f8c8d' }}>
              総お問い合わせ数: <strong>{total}</strong>件
            </span>
          </div>
          <div>
            <span style={{ fontSize: '16px', color: '#e74c3c' }}>
              未読: <strong>{unreadTotal}</strong>件
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            color: '#495057',
            cursor: 'pointer'
          }}
        >
          <input
            type="checkbox"
            checked={allSelectedOnPage}
            onChange={toggleSelectAll}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          このページを全選択
        </label>

        {selectedIds.size > 0 && (
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            style={{
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              padding: '10px 20px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            選択した{selectedIds.size}件を削除
          </button>
        )}

        <select
          value={filterStatus}
          onChange={(e) => handleFilterChange(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            fontSize: '14px'
          }}
        >
          <option value="all">すべて</option>
          <option value="unread">未読</option>
          <option value="read">既読</option>
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>表示件数:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
            style={{
              padding: '5px 10px',
              border: '1px solid #ddd',
              borderRadius: '3px',
              fontSize: '14px'
            }}
          >
            <option value={25}>25件</option>
            <option value={50}>50件</option>
            <option value={100}>100件</option>
            <option value={200}>200件</option>
          </select>
        </div>
      </div>

      {renderPagination()}

      {inquiries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
          お問い合わせはありません
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {inquiries.map((inquiry) => (
            <div
              key={inquiry._id}
              style={{
                backgroundColor: selectedIds.has(inquiry._id) ? '#e8f4f8' : (inquiry.is_read ? 'white' : '#fffbe6'),
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #ecf0f1',
                borderLeft: inquiry.is_read ? '4px solid #27ae60' : '4px solid #e74c3c'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(inquiry._id)}
                    onChange={() => toggleSelect(inquiry._id)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '4px' }}
                  />
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', color: '#2c3e50' }}>{inquiry.title}</h3>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: '#7f8c8d', fontSize: '14px' }}>
                        投稿者: {inquiry.userID?.name}
                      </span>
                      <span style={{ color: '#7f8c8d', fontSize: '14px' }}>
                        投稿日時: {new Date(inquiry.created_at).toLocaleString('ja-JP')}
                      </span>
                      <span style={{
                        backgroundColor: inquiry.is_read ? '#27ae60' : '#e74c3c',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {inquiry.is_read ? '既読' : '未読'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '15px',
                borderLeft: '4px solid #3498db'
              }}>
                <div style={{
                  margin: 0,
                  lineHeight: '1.6',
                  color: '#2c3e50',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxHeight: expandedInquiries.has(inquiry._id) ? 'none' : '50px',
                  overflow: expandedInquiries.has(inquiry._id) ? 'visible' : 'hidden',
                  position: 'relative'
                }}>
                  {inquiry.message}
                  {!expandedInquiries.has(inquiry._id) && inquiry.message && inquiry.message.length > 100 && (
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      background: 'linear-gradient(transparent, #f8f9fa)',
                      height: '50px',
                      width: '100%',
                      pointerEvents: 'none'
                    }}></div>
                  )}
                </div>
                {inquiry.message && inquiry.message.length > 100 && (
                  <div
                    onClick={() => toggleInquiryExpansion(inquiry._id)}
                    style={{
                      fontSize: '12px',
                      color: '#3498db',
                      marginTop: '5px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {expandedInquiries.has(inquiry._id) ? '▲ 折りたたむ' : '▼ 全文を表示'}
                  </div>
                )}
              </div>

              {/* Replies */}
              {inquiry.replies && inquiry.replies.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>返信履歴:</h4>
                  {inquiry.replies.map((replyItem, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: '#e8f5e8',
                        padding: '10px',
                        borderRadius: '5px',
                        marginBottom: '10px',
                        borderLeft: '4px solid #27ae60'
                      }}
                    >
                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                        {replyItem.adminId?.name} - {new Date(replyItem.createdAt).toLocaleString('ja-JP')}
                      </div>
                      <div style={{
                        margin: 0,
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        maxHeight: expandedReplies.has(`${inquiry._id}-${index}`) ? 'none' : '50px',
                        overflow: expandedReplies.has(`${inquiry._id}-${index}`) ? 'visible' : 'hidden',
                        position: 'relative'
                      }}>
                        {replyItem.reply}
                        {!expandedReplies.has(`${inquiry._id}-${index}`) && replyItem.reply && replyItem.reply.length > 100 && (
                          <div style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '0',
                            background: 'linear-gradient(transparent, #e8f5e8)',
                            height: '50px',
                            width: '100%',
                            pointerEvents: 'none'
                          }}></div>
                        )}
                      </div>
                      {replyItem.reply && replyItem.reply.length > 100 && (
                        <div
                          onClick={() => toggleReplyExpansion(`${inquiry._id}-${index}`)}
                          style={{
                            fontSize: '12px',
                            color: '#3498db',
                            marginTop: '5px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          {expandedReplies.has(`${inquiry._id}-${index}`) ? '▲ 折りたたむ' : '▼ 全文を表示'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ color: '#7f8c8d', fontSize: '14px' }}>
                  メール: {inquiry.userID?.email}
                </span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {!inquiry.is_read && (
                    <button
                      onClick={() => markAsRead(inquiry._id)}
                      style={{
                        backgroundColor: '#27ae60',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      既読にする
                    </button>
                  )}
                  <button
                    onClick={() => handleReply(inquiry)}
                    style={{
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    返信する
                  </button>
                  <button
                    onClick={() => handleDelete(inquiry)}
                    style={{
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    削除する
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {renderPagination()}

      {/* Reply Modal */}
      {showReplyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '600px',
            maxWidth: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              {selectedInquiry?.userID?.name}さんへの返信
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>元の問い合わせ:</h4>
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                borderLeft: '4px solid #3498db'
              }}>
                <p style={{ margin: 0, lineHeight: '1.6' }}>
                  {selectedInquiry?.message}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>返信内容:</h4>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="返信内容を入力してください..."
                style={{
                  width: '100%',
                  height: '150px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowReplyModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendReply}
                disabled={!reply.trim()}
                style={{
                  backgroundColor: reply.trim() ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: reply.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                送信
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      {showDeleteModal && inquiryToDelete && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#e74c3c' }}>お問い合わせ削除</h3>
            <p>このお問い合わせを削除してもよろしいですか？</p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '5px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>{inquiryToDelete.title}</strong>
            </div>
            <p style={{ color: '#e74c3c', fontSize: '14px', marginTop: '-10px', marginBottom: '20px' }}>
              この操作は取り消せません。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setInquiryToDelete(null); }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={deleteInquiry}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#e74c3c' }}>一括削除</h3>
            <p>選択した<strong>{selectedIds.size}件</strong>のお問い合わせを削除してもよろしいですか？</p>
            <p style={{ color: '#e74c3c', fontSize: '14px', marginTop: '10px' }}>
              この操作は取り消せません。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleBulkDelete}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryList;
